var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Autocomplete, Box, CircularProgress, InputAdornment, Paper, TextField, useTheme } from '@mui/material';
import { baseColors } from '../../../Themes/colors';
import FilterChip from './FilterChip';
var BacklogFilterSelect = function (_a) {
    var _b;
    var options = _a.options, displayKey = _a.displayKey, valueKey = _a.valueKey, selectedValue = _a.selectedValue, placeholder = _a.placeholder, _c = _a.multiple, multiple = _c === void 0 ? false : _c, onSelectionChange = _a.onSelectionChange, onOpen = _a.onOpen, onInputChange = _a.onInputChange, onLoadMore = _a.onLoadMore, _d = _a.loading, loading = _d === void 0 ? false : _d, _e = _a.loadingMore, loadingMore = _e === void 0 ? false : _e, _f = _a.serverSideFilter, serverSideFilter = _f === void 0 ? false : _f, errorText = _a.errorText, getOptionDisabled = _a.getOptionDisabled;
    var _g = React.useState(''), inputTextValue = _g[0], setInputTextValue = _g[1];
    var getOptionIdentity = React.useCallback(function (option) {
        var _a, _b;
        var valuePart = String((_a = option === null || option === void 0 ? void 0 : option[valueKey]) !== null && _a !== void 0 ? _a : '');
        var displayPart = String((_b = option === null || option === void 0 ? void 0 : option[displayKey]) !== null && _b !== void 0 ? _b : '');
        return "".concat(valuePart, "::").concat(displayPart);
    }, [displayKey, valueKey]);
    var normalizedSelectedValues = React.useMemo(function () {
        if (multiple) {
            return Array.isArray(selectedValue) ? selectedValue.filter(Boolean) : [];
        }
        return selectedValue ? [selectedValue] : [];
    }, [multiple, selectedValue]);
    var normalizedOptionMap = React.useMemo(function () {
        var map = new Map();
        (Array.isArray(options) ? options : []).forEach(function (option) {
            map.set(getOptionIdentity(option), option);
        });
        return map;
    }, [getOptionIdentity, options]);
    var matchedSelectedValues = React.useMemo(function () { return normalizedSelectedValues.filter(function (option) { return normalizedOptionMap.has(getOptionIdentity(option)); }); }, [getOptionIdentity, normalizedOptionMap, normalizedSelectedValues]);
    var autocompleteValue = multiple
        ? matchedSelectedValues
        : (_b = matchedSelectedValues[0]) !== null && _b !== void 0 ? _b : null;
    var handleRemove = function (optionToRemove) {
        if (multiple) {
            var removeIdentity_1 = getOptionIdentity(optionToRemove);
            var nextValue = normalizedSelectedValues.filter(function (option) { return getOptionIdentity(option) !== removeIdentity_1; });
            onSelectionChange(nextValue);
            return;
        }
        onSelectionChange(null);
    };
    var handleListboxScroll = function (event) {
        if (!onLoadMore || loading || loadingMore)
            return;
        var listboxNode = event.currentTarget;
        var threshold = 32;
        var reachedBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - threshold;
        if (reachedBottom) {
            onLoadMore();
        }
    };
    var loadingMoreRef = React.useRef(loadingMore);
    loadingMoreRef.current = loadingMore;
    var renderPaper = React.useCallback(function (_a) {
        var children = _a.children, paperProps = __rest(_a, ["children"]);
        return (React.createElement(Paper, __assign({}, paperProps),
            children,
            loadingMoreRef.current && (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', py: 1 } },
                React.createElement(CircularProgress, { size: 16 })))));
    }, []);
    var theme = useTheme();
    return (React.createElement(Box, null,
        normalizedSelectedValues.length > 0 && (React.createElement(Box, { sx: {
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                mb: '10px',
            } }, normalizedSelectedValues.map(function (option, index) {
            var _a;
            return (React.createElement(FilterChip, { key: "".concat(getOptionIdentity(option), "-").concat(index), label: String((_a = option === null || option === void 0 ? void 0 : option[displayKey]) !== null && _a !== void 0 ? _a : ''), onRemove: function () { return handleRemove(option); }, sx: { maxWidth: '100%' } }));
        }))),
        React.createElement(Autocomplete, __assign({ multiple: multiple, disableCloseOnSelect: multiple, options: options, value: autocompleteValue, popupIcon: React.createElement("span", { className: "icon-arrow_drop_down", style: { fontSize: 16, fontWeight: 700 } }), clearIcon: React.createElement(React.Fragment, null), getOptionLabel: function (option) { var _a; return String((_a = option === null || option === void 0 ? void 0 : option[displayKey]) !== null && _a !== void 0 ? _a : ''); }, isOptionEqualToValue: function (option, value) {
                return getOptionIdentity(option) === getOptionIdentity(value);
            }, onChange: function (_, value) { return onSelectionChange(value); }, onOpen: onOpen }, (serverSideFilter ? { inputValue: inputTextValue } : {}), { onInputChange: function (_, value, reason) {
                if (reason === 'input') {
                    if (serverSideFilter) {
                        setInputTextValue(value);
                    }
                    onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(value);
                }
                else if (serverSideFilter && reason === 'clear') {
                    setInputTextValue(value);
                }
            }, filterOptions: serverSideFilter ? function (opts) { return opts; } : undefined, loading: loading, loadingText: "Loading...", noOptionsText: errorText || 'No Options', ListboxProps: { onScroll: handleListboxScroll }, PaperComponent: renderPaper, renderTags: function () { return null; }, getOptionDisabled: getOptionDisabled, renderOption: function (props, option, _a) {
                var _b;
                var selected = _a.selected;
                return (React.createElement("li", __assign({}, props, { style: __assign(__assign({}, (props.style || {})), { fontSize: '12px', color: '#344767', backgroundColor: selected
                            ? baseColors.bluePreset[60]
                            : baseColors.greyPreset[60], fontWeight: selected ? 600 : 500 }) }), String((_b = option === null || option === void 0 ? void 0 : option[displayKey]) !== null && _b !== void 0 ? _b : '')));
            }, sx: {
                width: '100%',
                '& .MuiOutlinedInput-root': {
                    height: 'auto',
                    padding: 8,
                    paddingLeft: '0px',
                    paddingRight: '32px !important',
                    borderRadius: '4px',
                    backgroundColor: baseColors.fullWhite,
                    '& fieldset': {
                        borderColor: baseColors.greyPreset[20],
                        borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                        borderColor: baseColors.successPreset[10],
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: baseColors.successPreset[30],
                        boxShadow: theme.shadows[1],
                    },
                },
                '& .MuiAutocomplete-inputRoot': {
                    minHeight: '28px',
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important',
                    paddingLeft: '8px !important',
                },
                '& .MuiAutocomplete-input': {
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    fontWeight: 400,
                    color: baseColors.darkPreset[10],
                    letterSpacing: 'normal',
                    '&::placeholder': {
                        color: baseColors.greyPreset[5],
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        fontWeight: 400,
                        letterSpacing: 'normal',
                        opacity: 1,
                    },
                },
                '& .MuiAutocomplete-endAdornment': {
                    right: '5px !important',
                    top: 'calc(50%)',
                },
                '& .MuiAutocomplete-popupIndicator': {
                    padding: '0px',
                },
                '& .MuiAutocomplete-option': {
                    backgroundColor: baseColors.greyPreset[60],
                    '&:hover': {
                        backgroundColor: baseColors.bluePreset[60],
                    },
                    '&[aria-selected="true"]': {
                        backgroundColor: "".concat(baseColors.bluePreset[60], " !important"),
                    },
                },
                '& .MuiInputAdornment-root': {
                    marginRight: '4px',
                },
            }, renderInput: function (params) { return (React.createElement(TextField, __assign({}, params, { placeholder: placeholder, InputProps: __assign(__assign({}, params.InputProps), { startAdornment: (React.createElement(InputAdornment, { position: "start" },
                        React.createElement("span", { className: "icon-Research", style: { color: baseColors.greyPreset[10], fontSize: 13, } }))) }), inputProps: __assign(__assign({}, params.inputProps), { style: {
                        padding: 0,
                    } }) }))); } }))));
};
export default BacklogFilterSelect;
